/**
 * Reusable Learn topic model.
 *
 * A topic (IFS, Attachment Styles, ...) is a tree:
 *
 *   Topic → Section → Resource → Block[]
 *
 * Everything is plain, serialisable data so new guides, exercises,
 * worksheets, prompts, audio or video can be added by editing a data file
 * only — no page, route or layout changes required.
 */

/** The five reusable content formats shown across Learn. */
export type ContentKind = "learn" | "practice" | "reflect" | "worksheet" | "audio" | "video";

export const CONTENT_KINDS: Record<
  ContentKind,
  { emoji: string; label: string; blurb: string; chip: string }
> = {
  learn: {
    emoji: "📚",
    label: "Learn",
    blurb: "Educational guide",
    chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200",
  },
  practice: {
    emoji: "🌿",
    label: "Practice",
    blurb: "Guided exercise",
    chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
  },
  reflect: {
    emoji: "✍️",
    label: "Reflect",
    blurb: "Journal prompts",
    chip: "bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200",
  },
  worksheet: {
    emoji: "📄",
    label: "Worksheet",
    blurb: "Printable resource",
    chip: "bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100",
  },
  audio: {
    emoji: "🎧",
    label: "Guided practice",
    blurb: "Audio exercise",
    chip: "bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200",
  },
  video: {
    emoji: "🎬",
    label: "Video",
    blurb: "Watch and follow",
    chip: "bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200",
  },
};

/** Content blocks a resource page can be built from. */
export type ResourceBlock =
  | { type: "prose"; text: string }
  | { type: "list"; title?: string; items: string[] }
  | { type: "steps"; title?: string; items: { label: string; note?: string }[] }
  | { type: "prompts"; title?: string; items: string[] }
  /** Vertical arrow chain, e.g. Part name ↓ Emotion ↓ Trigger. */
  | { type: "flow"; title?: string; items: string[] }
  | { type: "quote"; text: string }
  /** Gentle caveat / pacing note, rendered in a soft callout. */
  | { type: "note"; text: string }
  /** Gentle call-to-action link, e.g. to the Abuse Journal. */
  | { type: "cta"; text: string; label: string; to: string }
  /** Named illustration rendered by the resource view. */
  | { type: "visual"; name: "ifs-parts" | "attachment-quadrants" }
  | { type: "audio"; src: string; label?: string }
  | { type: "video"; src: string; label?: string };

/** A single piece of content inside a section. */
export type TopicResource = {
  slug: string;
  kind: ContentKind;
  title: string;
  summary: string;
  minutes?: number;
  /** Omit for available content; "soon" renders a calm placeholder. */
  status?: "soon";
  blocks?: ResourceBlock[];
  /** Printable worksheet lines. Rendered with a Print button. */
  worksheetFields?: { label: string; hint?: string; lines?: number }[];
};

export type TopicSection = {
  slug: string;
  emoji: string;
  title: string;
  blurb: string;
  /** Optional longer intro shown at the top of the section page. */
  intro?: string;
  resources: TopicResource[];
};

/** Hub categories used to group topics on the Learn index. */
export type LearnCategory =
  | "understanding-trauma"
  | "cptsd-responses"
  | "nervous-system"
  | "cbt-dbt"
  | "ifs"
  | "relationships"
  | "bonds-boundaries"
  | "shame-self-worth"
  | "grief-loss"
  | "sleep"
  | "safety";

export type LearnPathway =
  | "trauma-types"
  | "symptoms-responses"
  | "relationships-abuse"
  | "nervous-system"
  | "mental-health"
  | "recovery-healing";

/** A "Continue" pointer to another Learn module, tool or page. */
export type ContinueLink = { label: string; to: string; note?: string };

export type LearnTopic = {
  slug: string;
  emoji: string;
  title: string;
  tagline: string;
  /** Paragraphs shown on the topic overview. */
  intro: string[];
  /** Key of LEARN_THEMES. */
  theme: "sky" | "lavender" | "sage" | "peach" | "gold";
  seoDescription: string;
  /** Where this topic appears on the Learn hub. */
  category?: LearnCategory;
  /** Broad, user-facing routes through the growing library. */
  pathways?: LearnPathway[];
  /** Short card description used on the Learn hub. */
  cardBlurb?: string;
  /** Surface in "New in Learn 🌱". */
  isNew?: boolean;
  /** Suggested next modules/tools, shown at the end of the topic. */
  continueTo?: ContinueLink[];
  sections: TopicSection[];
  /** Optional closing note (kept trauma-informed and non-diagnostic). */
  closing?: string;

};

/** Flatten a topic into ordered resources, for prev/next navigation. */
export function flattenResources(topic: LearnTopic) {
  return topic.sections.flatMap((section) =>
    section.resources.map((resource) => ({ section, resource })),
  );
}

export function findSection(topic: LearnTopic, sectionSlug: string) {
  return topic.sections.find((s) => s.slug === sectionSlug) ?? null;
}

export function findResource(topic: LearnTopic, sectionSlug: string, resourceSlug: string) {
  return findSection(topic, sectionSlug)?.resources.find((r) => r.slug === resourceSlug) ?? null;
}

export function countByKind(topic: LearnTopic, kind: ContentKind) {
  return flattenResources(topic).filter((r) => r.resource.kind === kind).length;
}
